<template>
  <b-card title="Display Properties">
    <pipeline-status-alert
      pipeline-response-name="updateDisplayPropsResponse"
      for-type="property" for-role="field" :combined="true">
    </pipeline-status-alert>
    <b-form @submit.prevent="updateDisplayProperties">
      <b-form-group label="Display Name" label-for="displayNameField">
        <b-form-input id="displayNameField"
                      v-model="displayPropertiesForm.displayName">
        </b-form-input>
      </b-form-group>
      <b-form-group label="Group Contact Email Address"
                    label-for="groupContactField"
                    :class="{ 'invalid': $v.displayPropertiesForm.emailAddress.$error }">
        <b-form-input type="email" id="groupContactField"
                      v-model.trim="$v.displayPropertiesForm.emailAddress.$model"></b-form-input>
        <p id="err-valid-email" v-if="!$v.displayPropertiesForm.emailAddress.email"
           class="error-message">Please enter a valid email address.</p>
      </b-form-group>
      <b-form-group label="Suppression Attributes">
        <b-form-checkbox-group stacked v-model="displayPropertiesForm.suppression"
                               name="suppressionOptions">
          <b-form-checkbox v-for="option in suppressionOptions"
                           :key="option.property"
                           :value="option.property"
                           :id="option.id"
          >
            {{ option.text }}
          </b-form-checkbox>
        </b-form-checkbox-group>
      </b-form-group>
      <b-form-group label="Group Replication">
        <b-form-checkbox-group stacked v-model="displayPropertiesForm.replication"
                               name="groupReplication">
          <template v-for="option in replicationOptions">
            <div :key="option.property">
              <b-form-checkbox :value="option.property" :id="option.id"
                               style="display: inline-block">
                {{ option.text }}
              </b-form-checkbox>
              <div :id="option.property + '-info-container'"
                   class="text-info fa-icon-sm"
                   style="display: inline-block;">
                <font-awesome-icon :id="option.property + '-replication-info'" icon="info-circle"></font-awesome-icon>
              </div>
            </div>
          </template>
        </b-form-checkbox-group>
      </b-form-group>
      <button id="btn-display-properties" class="btn btn-primary"
              :disabled="$v.displayPropertiesForm.emailAddress.$invalid ||
                    displayPropertiesForm.submitting || !displayPropertiesChanged">
        {{displayPropertiesForm.submitting === true ? 'Submitting...' : 'Update Display Properties'}}
      </button>
    </b-form>
    <b-popover v-for="option in replicationOptions"
               :key="option.property"
               :target="option.property + '-replication-info'"
               :container="option.property + 'info-container'"
               :ref="option.property + '-popover'"
               :title="option.popoverRefName + ' Replication Info'"
               triggers="click blur"
               placement="auto">
      <template>
        Important information about replication to {{option.popoverRefName}} can be found
        <b-link :href="option.infoLink" target="_blank">
          here
        </b-link>
      </template>
    </b-popover>
  </b-card>
</template>

<script>
import { email } from 'vuelidate/lib/validators';
import PipelineStatusAlert from '../PipelineStatusAlert';

export default {
  name: 'EditDisplayProperties',
  components: {
    PipelineStatusAlert,
  },
  props: {
    managedGroup: {
      type: Object,
      required: true,
    },
    responseName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      displayPropertiesForm: {
        submitting: false,
        displayName: '',
        emailAddress: '',
        suppression: [],
        replication: [],
      },
      suppressionOptions: [
        { property: 'suppressDisplay', id: 'chk-suppress-display', text: 'Suppress Display' },
        { property: 'suppressMembers', id: 'chk-suppress-members', text: 'Suppress Members' },
      ],
      replicationOptions: [
        {
          edTarget: 'google.com:a:groups.vt.edu',
          property: 'googleGroup',
          id: 'chk-google-group',
          text: 'Google Group',
          popoverRefName: 'Google',
          infoLink: 'https://www.middleware.vt.edu/ed/groups/#google-groups',
        },
        {
          edTarget: 'w2k.vt.edu',
          property: 'adGroup',
          id: 'chk-active-directory',
          text: 'Active Directory Group',
          value: 'adGroup',
          popoverRefName: 'Active Directory',
          infoLink: 'https://www.middleware.vt.edu/ed/groups/#ad-groups',
        },
        {
          edTarget: 'code.vt.edu',
          property: 'gitGroup',
          id: 'chk-gitlab',
          text: 'GitLab Group',
          value: 'gitGroup',
          popoverRefName: 'GitLab',
          infoLink: 'https://www.middleware.vt.edu/ed/groups/#gitlab-groups',
        },
      ],
    };
  },
  computed: {
    displayPropertiesChanged() {
      const displayProperties = [
        'displayName',
        'emailAddress',
        'suppressDisplay',
        'suppressMembers',
        'googleGroup',
        'adGroup',
        'gitGroup',
      ];
      let propHasChanged = false;
      displayProperties.forEach((property) => {
        if (this.displayPropertyChanged(this.displayPropertiesForm, property)) {
          propHasChanged = true;
        }
      });
      return propHasChanged;
    },
  },
  methods: {
    /* Compare top-level attributes of form backing display properties object with the current state object. */
    displayPropertyChanged(formModel, property) {
      if (property === 'googleGroup' || property === 'adGroup' || property === 'gitGroup') {
        return formModel.replication.includes(property) !== this.managedGroup.replication[property];
      }
      if (property === 'suppressDisplay' || property === 'suppressMembers') {
        return formModel.suppression.includes(property) !== !!this.managedGroup[property];
      }
      return formModel[property] !== this.managedGroup[property];
    },
    updateDisplayProperties() {
      const vm = this;
      this.$v.$touch();
      if (!this.$v.$invalid) {
        vm.displayPropertiesForm.submitting = true;
        const pipelinedPromises = [];
        const uugid = this.managedGroup.uugid;
        /* Get list of display property patches for displayName, emailAddress, suppressDisplay, and suppressMembers */
        const patches = this.displayPropertyPatches();
        if (patches.length > 0) {
          pipelinedPromises.push(
            {
              item: uugid,
              promise: vm.$store.dispatch('doDisplayPropertiesUpdate', { uugid, patches }),
            },
          );
        }
        /* Updating the Group Target identifiers cannot be submitted as a patch with the other display property changes
           * so it is added as it's own promise/request. */
        this.replicationOptions.forEach((option) => {
          if (vm.displayPropertyChanged(vm.displayPropertiesForm, option.property)) {
            pipelinedPromises.push(this.updateTargetPromise(option.edTarget, option.property, uugid));
          }
        });
        /* Dispatch requests */
        vm.$store.dispatch('pipelineRequest',
          { uugid, pipelinedPromises, responseToSet: 'updateDisplayPropsResponse' })
          .finally(() => {
            vm.setDisplayPropertiesFormData();
            vm.displayPropertiesForm.submitting = false;
          });
      }
    },
    displayPropertyPatches() {
      const vm = this;
      const patches = [];
      const patchPropertyOperations = [
        {
          property: 'displayName',
          operation: vm.displayPropertiesForm.displayName ? 'replace' : 'remove',
          newVal: vm.displayPropertiesForm.displayName,
        },
        {
          property: 'emailAddress',
          operation: vm.displayPropertiesForm.emailAddress ? 'replace' : 'remove',
          newVal: vm.displayPropertiesForm.emailAddress,
        },
        {
          property: 'suppressDisplay',
          operation: 'replace',
          newVal: vm.displayPropertiesForm.suppression.includes('suppressDisplay'),
        },
        {
          property: 'suppressMembers',
          operation: 'replace',
          newVal: vm.displayPropertiesForm.suppression.includes('suppressMembers'),
        },
      ];
      patchPropertyOperations.forEach((propertyOperation) => {
        if (vm.displayPropertyChanged(vm.displayPropertiesForm, propertyOperation.property)) {
          patches.push(
            {
              op: propertyOperation.operation,
              path: `/${propertyOperation.property}`,
              value: propertyOperation.newVal,
            });
        }
      });
      return patches;
    },
    /* Create and return a promise to update group replication for a specific target (Google, AD, GitLab) */
    updateTargetPromise(edIdentifier, formProp, uugid) {
      const vm = this;
      const targetAction =
          (vm.displayPropertiesForm.replication.includes(formProp)) ? 'addGroupTarget' : 'removeGroupTarget';
      return {
        item: uugid,
        promise: vm.$store.dispatch(targetAction,
          { uugid, system: edIdentifier }),
      };
    },
    /* Set existing replication data in the form backing object based on the data in the currently selected group.
       * Used to prepopulate the form input fields with the current group data. */
    setCurrentReplicationFormProps() {
      this.displayPropertiesForm.replication = [];
      this.replicationOptions.forEach((replOption) => {
        if (this.managedGroup.replication[replOption.property]) {
          this.displayPropertiesForm.replication.push(replOption.property);
        }
      });
    },
    /* Set existing suppression data in the form backing object based on the data in the currently selected group.
       * Used to prepopulate the form input fields with the current group data. */
    setCurrentSuppressionFormProps() {
      this.displayPropertiesForm.suppression = [];
      this.suppressionOptions.forEach((supOption) => {
        if (this.managedGroup[supOption.property]) {
          this.displayPropertiesForm.suppression.push(supOption.property);
        }
      });
    },
    /* Set existing displayName in the form backing object based on the data in the currently selected group.
       * Used to prepopulate the form input fields with the current group data. */
    setCurrentFormDisplayName() {
      this.displayPropertiesForm.displayName = this.managedGroup.displayName;
    },
    /* Set existing emailAddress in the form backing object based on the data in the currently selected group.
       * Used to prepopulate the form input fields with the current group data. */
    setCurrentFormEmailAddress() {
      this.displayPropertiesForm.emailAddress = this.managedGroup.emailAddress;
    },
    /* Set all existing group data in the form backing object based on the data in the currently selected group.
       * Used to prepopulate the form input fields with the current group data. */
    setDisplayPropertiesFormData() {
      this.setCurrentFormDisplayName();
      this.setCurrentFormEmailAddress();
      this.setCurrentReplicationFormProps();
      this.setCurrentSuppressionFormProps();
    },
  },
  validations: {
    displayPropertiesForm: {
      emailAddress: { email },
    },
  },
  activated() {
    this.setDisplayPropertiesFormData();
  },
};
</script>

<style scoped>
  .fa-icon-sm {
    font-size: .9em;
  }
</style>



// WEBPACK FOOTER //
// src/components/edit-group/EditDisplayProperties.vue